import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule, KeyValue } from '@angular/common';

import { PipesModule } from '@jht/brand-pipes';
import { TranslateModule } from '@jht/brand-translate';

import { ProductItem } from '@src/app/shared/models/product';

interface ProductAttribute {
    attributeGroup?: string;
    attributeMarketingName: string;
    attributeMarketingValue: string;
    name: string;
    value: string;
    sortIndex: number;
}

@Component({
    selector: 'vision2-section-7',
    imports: [CommonModule, PipesModule, TranslateModule],
    templateUrl: './section-7.component.html',
    styleUrls: ['./section-7.component.scss'],
})
export class Section7Component implements OnChanges {
    @Input() productData!: ProductItem;
    groupedAttributes!: Record<string, ProductAttribute[]>;
    activeAccordionIndex: number | null = 0;
    readonly originalOrder = (): number => 0;

    constructor() {}

    ngOnChanges(changes: SimpleChanges) {
        this.setActiveConsole(changes.productData.currentValue);
    }

    setActiveConsole(productData: ProductItem): void {
        this.productData = productData;

        if (this.productData.activeConsole) {
            this.filterConsoleSpecsByFrameCategory(
                this.productData.activeConsole,
                productData.pdbMediaTag,
            );
            const consoleAttributes =
                this.productData.activeConsole.webVisibleProductAttributes ||
                [];
            const combinedAttributes = [
                ...this.productData.webVisibleProductAttributes,
                ...consoleAttributes,
            ];

            if (this.productData?.webVisibleProductAttributes) {
                this.groupedAttributes =
                    this.groupAttributesByGroup(combinedAttributes);
            }
        } else {
            this.groupedAttributes = this.groupAttributesByGroup(
                this.productData.webVisibleProductAttributes,
            );
        }
    }

    toggleAccordion(index: number): void {
        this.activeAccordionIndex =
            this.activeAccordionIndex === index ? null : index;
    }

    isAccordionOpen(index: number): boolean {
        return this.activeAccordionIndex === index;
    }

    private filterConsoleSpecsByFrameCategory(console: any, mediaTag: string) {
        if (console && console.webVisibleProductAttributes && mediaTag) {
            let attributes = console.webVisibleProductAttributes;
            console.webVisibleProductAttributes = attributes.filter(
                (spec) =>
                    !spec.frameCategory ||
                    spec.frameCategory.toLowerCase() === mediaTag,
            );
        }
    }

    private groupAttributesByGroup(
        attributes: ProductAttribute[],
    ): Record<string, ProductAttribute[]> {
        const grouped = attributes.reduce((acc, attr) => {
            const group = attr.attributeGroup || 'Other';
            if (!acc[group]) {
                acc[group] = [];
            }
            acc[group].push(attr);
            return acc;
        }, {} as Record<string, ProductAttribute[]>);

        // Sort items in each group by sortIndex
        Object.keys(grouped).forEach((group) => {
            grouped[group].sort(
                (a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0),
            );
        });

        // Sort groups alphabetically
        const sortedGroups = Object.keys(grouped)
            .sort((a, b) => a.localeCompare(b))
            .reduce((acc, key) => {
                acc[key] = grouped[key];
                return acc;
            }, {} as Record<string, ProductAttribute[]>);

        return sortedGroups;
    }
}
